import React from "react";
import { useQuery } from "@tanstack/react-query";
import { analyticsStatus, shopifyStatus, listAssets } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { CheckCircle, Circle, Warning, Rocket, ShieldCheck, Plug } from "@phosphor-icons/react";

const COMPLETED = [
  "Executive Marketing Dashboard (unified command center)",
  "AI Executive Assistant — daily brief (recommendation-only)",
  "Executive KPI Dashboard (10 live KPI cards)",
  "One-Click Marketing Command Center (14 actions)",
  "AI Marketing Director (full campaign generation)",
  "AI Content Studio + Image Studio (brand-safe overlays)",
  "Brand Manager (multi-brand workspaces)",
  "Approval Queue (bulk actions, comments, version history, grouping)",
  "Marketing Calendar / Scheduler (drag-and-drop)",
  "GA4 analytics (read-only)",
  "Meta & Google Ads analytics (read-only)",
  "Shopify read-only sync (OAuth)",
  "Approval-based Shopify WRITE system (two-step gate + activity log)",
  "Conversion Optimizer, SEO, Email/SMS, Promotions, Reports",
];

function Row({ ok, label, detail, warn, tag, tagColor }) {
  const Icon = ok ? CheckCircle : warn ? Warning : Circle;
  const color = ok ? "text-emerald-400" : warn ? "text-amber-400" : "text-[#7a7a7a]";
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-white/6 last:border-0" data-testid={`setup-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <Icon size={18} className={`${color} mt-0.5 shrink-0`} weight={ok ? "fill" : "regular"} />
      <div>
        <div className="text-sm text-[#cfcfcf] flex items-center gap-2 flex-wrap">
          {label}
          {tag && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tagColor || "bg-white/5 text-[#9a9a9a] border-white/10"}`}>{tag}</span>}
        </div>
        {detail && <div className="text-xs text-[#7a7a7a] mt-0.5">{detail}</div>}
      </div>
    </div>
  );
}

const TAG_LIVE = "bg-emerald-500/15 text-emerald-400 border-emerald-500/25";
const TAG_RECOMMENDED = "bg-amber-500/10 text-amber-400 border-amber-500/25";
const TAG_OPTIONAL = "bg-white/5 text-[#9a9a9a] border-white/10";

export default function LaunchChecklistPage() {
  const { activeId, activeClient } = useClient();
  const aStatus = useQuery({ queryKey: ["analytics-status", activeId], queryFn: () => analyticsStatus(activeId), enabled: !!activeId });
  const sStatus = useQuery({ queryKey: ["shopify-status", activeId], queryFn: () => shopifyStatus(activeId), enabled: !!activeId });
  const assetsQ = useQuery({ queryKey: ["assets", activeId], queryFn: () => listAssets(activeId), enabled: !!activeId });

  const ga4 = aStatus.data?.ga4?.connected;
  const meta = aStatus.data?.meta?.connected;
  const google = aStatus.data?.google?.connected;
  const shopify = sStatus.data?.connected;
  const shopifyScopesOk = shopify && !(sStatus.data?.missing_scopes || []).length;
  const hasAssets = (assetsQ.data || []).length > 0;

  const core = [
    { ok: true, label: "V1 platform features complete", tag: "Live", tagColor: TAG_LIVE, detail: "All Vozelto 1.0 features are built, tested and stable." },
    { ok: shopify, warn: shopify && !shopifyScopesOk, label: "Shopify store", tag: shopify ? "Live" : "Not connected", tagColor: shopify ? TAG_LIVE : TAG_OPTIONAL, detail: shopify ? (shopifyScopesOk ? "Connected with all read scopes — live products, orders, customers & revenue." : `Connected — missing scopes: ${(sStatus.data?.missing_scopes || []).join(", ")}`) : "Read-only OAuth on the Shopify page." },
    { ok: hasAssets, label: "Official brand assets", tag: hasAssets ? "Live" : "Not uploaded", tagColor: hasAssets ? TAG_LIVE : TAG_OPTIONAL, detail: hasAssets ? "Brand artwork uploaded — image overlays enabled." : "Upload logo/packaging in Brand Manager so AI images stay on-brand." },
  ];
  const optional = [
    { ok: ga4, label: "Google Analytics 4", tag: ga4 ? "Live" : "Optional — recommended", tagColor: ga4 ? TAG_LIVE : TAG_RECOMMENDED, detail: ga4 ? "Live traffic, sessions and conversion data." : "Not connected — the GA4 page and traffic widgets show honest empty states until connected. Connect on /ga4." },
    { ok: meta, label: "Meta Ads", tag: meta ? "Live" : "Optional", tagColor: meta ? TAG_LIVE : TAG_OPTIONAL, detail: meta ? "Live spend, campaigns and ROAS." : "Not connected — ad spend and ROAS screens show empty states until connected. Connect on /ads." },
    { ok: google, label: "Google Ads", tag: google ? "Live" : "Optional", tagColor: google ? TAG_LIVE : TAG_OPTIONAL, detail: google ? "Live campaign performance." : "Not connected — campaign performance shows empty states until connected. Connect on /ads." },
  ];

  const coreDone = core.filter((s) => s.ok).length;
  const readiness = Math.round((coreDone / core.length) * 100);
  const coreReady = coreDone === core.length;
  const optCount = optional.filter((s) => s.ok).length;

  return (
    <PageContainer>
      <PageHeader eyebrow="Version 1.0" title="Launch Readiness"
        subtitle={`Readiness for ${activeClient?.name}. The score reflects the core platform only — optional marketing integrations are tracked separately and their screens show honest empty states until connected. Nothing publishes automatically.`} />

      <div className="card-matte p-6 mb-6 brand-glow" data-testid="readiness-score">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#6B3DFF]/15 flex items-center justify-center"><Rocket size={26} className="brand-text" weight="fill" /></div>
            <div>
              <div className="font-head text-xl font-semibold">{coreReady ? "Core platform ready for production" : "Finish core setup to launch"}</div>
              <div className="text-sm text-[#9a9a9a]">{optCount}/3 optional marketing integrations connected — Vozelto shows no marketing analytics until they are.</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-head text-4xl font-semibold brand-text">{readiness}%</div>
            <div className="label-caps">Core Readiness</div>
          </div>
        </div>
        <div className="h-2 bg-white/10 rounded-full mt-4 overflow-hidden"><div className="h-full bg-[#6B3DFF]" style={{ width: `${readiness}%` }} /></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div>
          <div className="card-matte p-5 mb-5" data-testid="core-readiness">
            <div className="label-caps mb-3 flex items-center gap-1"><ShieldCheck size={14} className="text-emerald-400" /> Core Platform (counts toward readiness)</div>
            <div>{core.map((s) => <Row key={s.label} {...s} />)}</div>
          </div>
          <div className="card-matte p-5 mb-5" data-testid="setup-items">
            <div className="label-caps mb-3 flex items-center gap-1"><Plug size={14} className="brand-text" /> Optional Marketing Integrations</div>
            <p className="text-xs text-[#7a7a7a] mb-2">These do not block launch. Until connected, the related dashboards display empty states — never sample metrics.</p>
            <div>{optional.map((s) => <Row key={s.label} {...s} />)}</div>
          </div>
          <div className="card-matte p-5" data-testid="launch-blockers">
            <div className="label-caps mb-3 flex items-center gap-1"><Warning size={14} className="text-amber-400" /> Launch Blockers</div>
            {coreReady ? (
              <div className="flex items-center gap-2 text-sm text-emerald-400"><CheckCircle size={16} weight="fill" /> None — the core platform is stable and production-ready.</div>
            ) : core.filter((s) => !s.ok).map((b) => <Row key={b.label} warn label={b.label} detail={b.detail} />)}
          </div>
        </div>
        <div className="card-matte p-5" data-testid="completed-features">
          <div className="label-caps mb-3 flex items-center gap-1"><CheckCircle size={14} className="text-emerald-400" weight="fill" /> Completed Features</div>
          <div>{COMPLETED.map((f) => <Row key={f} ok label={f} />)}</div>
        </div>
      </div>
    </PageContainer>
  );
}
